type DocumentLineUnit =
  | "count"
  | "gram"
  | "kilogram"
  | "ounce"
  | "pound"
  | { custom: string };

export interface DocumentLineDraft {
  account_id: string;
  description: string;
  amount: string;
  tax_rate_id?: string;
  sku_id?: string;
  quantity?: string;
  unit?: string;
  unit_price?: string;
}

interface DocumentLinePayload {
  account_id: string;
  description: string;
  amount_minor: number;
  tax_rate_id?: string;
  sku_id?: string;
  quantity_amount?: number;
  quantity_scale?: number;
  unit?: DocumentLineUnit;
  unit_price_cents?: number;
}

/** Serialize the common accounting line contract used by invoices, bills,
 * quotes, customer credits, and supplier credits. The entered line total is
 * authoritative; quantity and unit price are context only. */
export function toDocumentLinePayload(
  line: DocumentLineDraft,
  index: number,
  accountError = "account ID is required.",
  includeTax = true,
): DocumentLinePayload {
  if (!line.account_id.trim())
    throw new Error(`Line ${index + 1}: ${accountError}`);
  if (!line.description.trim())
    throw new Error(`Line ${index + 1}: description is required.`);
  const amount_minor = toPositiveMinor(line.amount);
  if (amount_minor === null)
    throw new Error(
      `Line ${index + 1}: amount must be a positive number (e.g. 12.50).`,
    );

  const payload: DocumentLinePayload = {
    account_id: line.account_id.trim(),
    description: line.description.trim(),
    amount_minor,
  };
  const tax_rate_id = (line.tax_rate_id ?? "").trim();
  if (includeTax && tax_rate_id) payload.tax_rate_id = tax_rate_id;
  const sku_id = (line.sku_id ?? "").trim();
  if (sku_id) payload.sku_id = sku_id;

  const quantity = quantityParts(line.quantity ?? "");
  const enteredUnit = (line.unit ?? "").trim();
  if (quantity) {
    if (!enteredUnit)
      throw new Error(
        `Line ${index + 1}: unit is required when quantity is entered.`,
      );
    payload.quantity_amount = quantity.amount;
    payload.quantity_scale = quantity.scale;
    payload.unit = unitValue(enteredUnit);
  } else if (enteredUnit) {
    throw new Error(
      `Line ${index + 1}: quantity is required when unit is entered.`,
    );
  }

  if ((line.unit_price ?? "").trim()) {
    const unit_price_cents = toNonNegativeMinor(line.unit_price ?? "");
    if (unit_price_cents === null)
      throw new Error(
        `Line ${index + 1}: unit price must be zero or greater and representable in cents.`,
      );
    payload.unit_price_cents = unit_price_cents;
  }
  return payload;
}

export function toBillLinePayload(
  lines: DocumentLineDraft[],
): CreateBillLineInput[] {
  return serializeLines(lines, (line, index) =>
    toDocumentLinePayload(line, index),
  );
}

export function toCreditNoteLinePayload(
  lines: DocumentLineDraft[],
): CreateCreditNoteLineInput[] {
  return serializeLines(lines, (line, index) =>
    toDocumentLinePayload(line, index, "pick a revenue account.", false),
  );
}

export function toSupplierCreditLinePayload(
  lines: DocumentLineDraft[],
): CreateSupplierCreditLineInput[] {
  return serializeLines(lines, (line, index) =>
    toDocumentLinePayload(line, index, "pick an expense account.", false),
  );
}

function serializeLines<T>(
  lines: DocumentLineDraft[],
  serialize: (line: DocumentLineDraft, index: number) => T,
): T[] {
  if (lines.length === 0) throw new Error("Add at least one line.");
  return lines.map(serialize);
}

/** Entered positive amount to minor units, or null when it cannot represent
 * a positive money value. Shared by document forms for their live totals.
 *
 * USD-only by contract: invoice/bill/quote/credit line payloads carry no
 * currency, so every document line is entered in whole cents today. Do not
 * reuse this for payment entry where a document currency is in scope — use
 * the currency-aware `toMinorUnits(amount, currency)` from `@/lib/money`
 * instead. */
export function toMinor(amount: string): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
}

function toPositiveMinor(amount: string): number | null {
  return toMinor(amount);
}

// Same USD-only cents contract as `toMinor` above (unit prices may be zero).
function toNonNegativeMinor(amount: string): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) ? minor : null;
}

function quantityParts(value: string): { amount: number; scale: number } | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = /^(\d+)(?:\.(\d+))?$/.exec(trimmed);
  if (!match) throw new Error("quantity must be a positive decimal number.");
  const fraction = match[2] ?? "";
  if (fraction.length > 18)
    throw new Error("quantity must have at most 18 decimal places.");
  const amount = BigInt(`${match[1]}${fraction}`);
  if (amount <= 0n) throw new Error("quantity must be positive.");
  if (amount > BigInt(Number.MAX_SAFE_INTEGER))
    throw new Error("quantity is too large to represent exactly.");
  return { amount: Number(amount), scale: fraction.length };
}

function unitValue(value: string): DocumentLineUnit {
  const trimmed = value.trim();
  const normalized = trimmed.toLowerCase();
  if (["count", "gram", "kilogram", "ounce", "pound"].includes(normalized))
    return normalized as Exclude<DocumentLineUnit, { custom: string }>;
  return { custom: trimmed };
}
import type {
  CreateBillLineInput,
  CreateCreditNoteLineInput,
  CreateSupplierCreditLineInput,
} from "@/lib/api";
